import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  applyToWorkshopWithPayment,
  uploadPaymentProof,
  workshopApi,
} from "../../services/workshopApi";
import { useUserContext } from "../../context/UserContext";
import PaymentInstructionsDisplay from "./PaymentInstructions";
import "./PaymentProofUpload.css";

const PaymentProofUpload = ({ workshopId: propWorkshopId }) => {
  const params = useParams();
  // Support both prop-based (from PersistentWorkshopsLayout) and route-based workshopId
  const workshopId = propWorkshopId || params.workshopId;
  const navigate = useNavigate();
  const { user: currentUser } = useUserContext();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [workshop, setWorkshop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isExistingParticipant, setIsExistingParticipant] = useState(false);
  const [formData, setFormData] = useState({
    paymentMethod: "bank_transfer",
    transactionId: "",
    notes: "", // Payment notes
    applicationNotes: "", // Application/motivation notes
  });

  useEffect(() => {
    fetchWorkshopDetails();
  }, [workshopId]);

  const fetchWorkshopDetails = async () => {
    try {
      setLoading(true);
      const response = await workshopApi.getWorkshopById(workshopId);
      if (response.success) {
        const workshopData = response.data.workshop;
        setWorkshop(workshopData);

        // Check if current user is already a participant
        if (currentUser && workshopData.participants) {
          const currentUserId = currentUser.id || currentUser._id;
          const isParticipant = workshopData.participants.some((p) => {
            const participantId =
              p.user?._id || p.user?.id || p.user || p._id || p.id;
            return participantId === currentUserId;
          });
          setIsExistingParticipant(isParticipant);
}
      }
    } catch (err) {
      setError("Failed to load workshop details");
} finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError("Please select a file to upload");
      return;
    }

    if (!formData.transactionId.trim()) {
      setError("Please enter the transaction ID");
      return;
    }

    if (!formData.applicationNotes.trim()) {
      setError("Please enter your application notes/motivation");
      return;
    }

    if (!workshop) {
      setError("Workshop details not loaded");
      return;
    }

    try {
      setUploading(true);
      setError(null);

      let response;

      if (isExistingParticipant) {
        // User is already a participant, just upload payment proof
        response = await uploadPaymentProof(workshopId, selectedFile, {
          amount: workshop.pricing?.amount || 0,
          paymentMethod: formData.paymentMethod,
          transactionId: formData.transactionId,
          notes: formData.notes, // Payment-specific notes
        });

        // If application notes are provided, update them separately
        if (formData.applicationNotes.trim()) {
          try {
            await workshopApi.editMyApplication(workshopId, {
              notes: formData.applicationNotes.trim(),
            });
          } catch (appError) {
// Don't fail the whole process if application notes update fails
          }
        }
      } else {
        // User is not a participant yet, apply with payment proof
        response = await applyToWorkshopWithPayment(
          workshopId,
          selectedFile,
          {
            amount: workshop.pricing?.amount || 0,
            paymentMethod: formData.paymentMethod,
            transactionId: formData.transactionId,
            notes: formData.notes, // Payment-specific notes
          },
          {
            motivation: formData.applicationNotes, // Application notes/motivation
            experience: "Provided via payment application",
          }
        );
      }

      if (response.success) {
        setSuccess(true);
        setSelectedFile(null);
        // Redirect back to workshop after 3 seconds
        setTimeout(() => {
          navigate(`/workshops/${workshopId}`);
        }, 3000);
      }
    } catch (err) {
      const errorMessage = isExistingParticipant
        ? "Failed to upload payment proof"
        : "Failed to apply to workshop with payment proof";
      setError(err.message || errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setSelectedFile(files[0]);
      setError(null);
    }
  };

  return (
    <div className="workshop-payment-proof-container">
      <div className="workshop-payment-proof-header">
        <h1>
          {isExistingParticipant
            ? "Upload Payment Proof"
            : "Apply to Workshop with Payment"}
        </h1>
        <p>
          {isExistingParticipant
            ? "Upload your payment receipt or proof of payment for this workshop"
            : "Complete your workshop application by uploading your payment proof"}
        </p>
        {workshop && (
          <div className="workshop-payment-info">
            <h2>{workshop.title}</h2>
            <p className="workshop-price">
              <strong>
                Amount: {workshop.pricing?.currency} {workshop.pricing?.amount}
              </strong>
            </p>

            {/* Show Payment Instructions */}
            {workshop.pricing?.paymentInstructions && (
              <PaymentInstructionsDisplay workshop={workshop} />
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="workshop-payment-loading-message">
          <p>Loading workshop details...</p>
        </div>
      ) : (
        <div className="workshop-payment-upload-section">
          {success ? (
            <div className="workshop-payment-success-message">
              <div className="workshop-payment-success-icon">✓</div>
              <h3>
                {isExistingParticipant
                  ? "Payment proof uploaded successfully!"
                  : "Application submitted successfully!"}
              </h3>
              <p>
                {isExistingParticipant
                  ? "Your payment proof has been submitted and is being reviewed."
                  : "Your workshop application with payment proof has been submitted and is being reviewed."}
              </p>
              <p>Redirecting back to workshop...</p>
            </div>
          ) : (
            <>
              {/* Payment Information Form */}
              <div className="workshop-payment-form">
                <h3>Payment Information</h3>

                <div className="workshop-payment-form-group">
                  <label htmlFor="paymentMethod">Payment Method:</label>
                  <select
                    id="paymentMethod"
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleInputChange}
                    className="workshop-payment-form-control"
                  >
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="credit_card">Credit Card</option>
                    <option value="paypal">PayPal</option>
                    <option value="cash">Cash</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="workshop-payment-form-group">
                  <label htmlFor="transactionId">Transaction ID *:</label>
                  <input
                    type="text"
                    id="transactionId"
                    name="transactionId"
                    value={formData.transactionId}
                    onChange={handleInputChange}
                    placeholder="Enter your transaction/reference ID"
                    className="workshop-payment-form-control"
                    required
                  />
                </div>

                <div className="workshop-payment-form-group">
                  <label htmlFor="applicationNotes">Application Notes *:</label>
                  <textarea
                    id="applicationNotes"
                    name="applicationNotes"
                    value={formData.applicationNotes}
                    onChange={handleInputChange}
                    placeholder="Why do you want to join this workshop? What's your motivation and relevant experience?"
                    className="workshop-payment-form-control"
                    rows="4"
                    required
                  />
                </div>

                <div className="workshop-payment-form-group">
                  <label htmlFor="notes">Payment Notes (Optional):</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Add any payment-specific information (e.g., payment method details, reference notes)..."
                    className="workshop-payment-form-control"
                    rows="3"
                  />
                </div>
              </div>

              {/* File Upload Section */}
              <div
                className={`workshop-file-drop-zone ${
                  selectedFile ? "has-file" : ""
                }`}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => document.getElementById("file-input").click()}
              >
                <input
                  id="file-input"
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                />

                {selectedFile ? (
                  <div className="workshop-file-selected">
                    <div className="workshop-file-icon">📄</div>
                    <div className="workshop-file-info">
                      <p className="workshop-file-name">{selectedFile.name}</p>
                      <p className="workshop-file-size">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="workshop-drop-zone-content">
                    <div className="workshop-upload-icon">📁</div>
                    <h3>Drop your payment proof here</h3>
                    <p>or click to browse files</p>
                    <p className="workshop-file-types">
                      Supports: JPG, PNG, PDF (max 10MB)
                    </p>
                  </div>
                )}
              </div>

              {error && (
                <div className="workshop-payment-error-message">
                  <p>{error}</p>
                </div>
              )}

              <div className="workshop-upload-actions">
                <button
                  onClick={() => navigate(`/workshops/${workshopId}`)}
                  className="workshop-cancel-btn"
                >
                  Cancel
                </button>
                {selectedFile && (
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="workshop-remove-file-btn"
                  >
                    Remove File
                  </button>
                )}
                <button
                  onClick={handleUpload}
                  disabled={
                    !selectedFile || !formData.transactionId.trim() || uploading
                  }
                  className="workshop-upload-btn"
                >
                  {uploading
                    ? isExistingParticipant
                      ? "Uploading..."
                      : "Submitting Application..."
                    : isExistingParticipant
                    ? "Upload Payment Proof"
                    : "Submit Application with Payment"}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <div className="workshop-upload-guidelines">
        <h3>Upload Guidelines</h3>
        <ul>
          <li>Make sure the payment proof is clear and readable</li>
          <li>Include transaction ID, amount, and date</li>
          <li>Accepted formats: JPG, PNG, PDF</li>
          <li>Maximum file size: 10MB</li>
          <li>
            {isExistingParticipant
              ? "Your payment will be verified within 24-48 hours"
              : "Your application and payment will be verified within 24-48 hours"}
          </li>
        </ul>
      </div>
    </div>
  );
};

export default PaymentProofUpload;
